/**
 * The background-music controls (ADR-0146): the **music volume slider** and **music
 * mute button** living in a Music section of the settings panel (`#settings-panel` in
 * `index.html`, shell wired by `settings.ts`), right beside the Sound (SFX) controls.
 * The music sibling of {@link ./sound}: same shape, same persistence discipline, but a
 * wholly independent channel - its own localStorage keys and its own audio gain node
 * ({@link ./audio/musicPlayer.MusicPlayer}), so the music slider affects **only** the
 * login-playlist music and never the SFX soundscape.
 *
 * Like `sound.ts` / `settings.ts`, this module owns **only** the DOM wiring; the markup
 * and styling live in `index.html`, and it touches no Pixi, no socket, and no Web Audio,
 * so it is unit-testable headlessly (jsdom).
 *
 * The mute button (`#music-button`) flips the music mute and reflects it (a 🎵/🔇 glyph
 * plus aria). The slider (`#music-slider`) rides the music volume live as it drags (or
 * via its arrow keys). Both choices persist in localStorage so they survive a reload -
 * exactly how `sound.ts` persists the SFX mute/level. Mute and level are independent:
 * muting silences without disturbing the level, so unmuting restores the prior loudness.
 * The persisted choices are pushed to the music player at startup (via
 * `onMuteChange`/`onVolumeChange`), so a player's music mix is restored before login.
 */

/** Local-storage key the player's music on/off (mute) choice persists under. */
const MUSIC_MUTE_KEY = 'britbank.musicMuted';
/** Local-storage key the player's music volume level (`0..100`) persists under. */
const MUSIC_VOLUME_KEY = 'britbank.musicVolume';

/**
 * Read the saved music-mute choice, defaulting to **unmuted** (music on) when nothing is
 * stored or storage is unavailable. Mirrors `sound.ts`'s `loadMuted`.
 */
function loadMuted(doc: Document): boolean {
  try {
    return windowOf(doc).localStorage.getItem(MUSIC_MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist the music-mute choice so it survives a reload/reconnect. */
function saveMuted(doc: Document, muted: boolean): void {
  try {
    windowOf(doc).localStorage.setItem(MUSIC_MUTE_KEY, muted ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/**
 * Read the saved music-volume level as a fraction in `[0, 1]`, defaulting to **full** (1)
 * when nothing is stored, the value is unparseable, or storage is unavailable. Stored as
 * an integer `0..100` to match the range input's units.
 */
function loadVolume(doc: Document): number {
  try {
    const raw = windowOf(doc).localStorage.getItem(MUSIC_VOLUME_KEY);
    if (raw === null) return 1;
    const pct = Number(raw);
    if (!Number.isFinite(pct)) return 1;
    return clamp01(pct / 100);
  } catch {
    return 1;
  }
}

/** Persist the music-volume level (as an integer percent) so it survives a reload. */
function saveVolume(doc: Document, level: number): void {
  try {
    windowOf(doc).localStorage.setItem(MUSIC_VOLUME_KEY, String(Math.round(clamp01(level) * 100)));
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/** Clamp a number into `[0, 1]`. */
function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}

/** The window owning a document (the live `window` in the app, the jsdom one in tests). */
function windowOf(doc: Document): Window & typeof globalThis {
  return (doc.defaultView ?? window) as Window & typeof globalThis;
}

/** Options for {@link initMusic}; `doc` is injected by tests. */
export interface MusicOptions {
  /**
   * Apply the music mute to the music player. Called once at startup with the persisted
   * choice (so a saved mute takes effect immediately) and again on every toggle.
   * `main.ts` wires this to `MusicPlayer.setMuted`.
   */
  onMuteChange: (muted: boolean) => void;
  /**
   * Apply the music volume level (`[0, 1]`) to the music player. Called once at startup
   * with the persisted choice and again live as the slider drags. `main.ts` wires this
   * to `MusicPlayer.setVolume`. Optional so tests need not pass it.
   */
  onVolumeChange?: (level: number) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the music controls through. */
export interface MusicHandle {
  /** Whether music is currently muted. */
  isMuted(): boolean;
  /** Set the music mute state programmatically (reflects, persists, and notifies). */
  setMuted(muted: boolean): void;
  /** The current music volume level in `[0, 1]`. */
  getVolume(): number;
}

export function initMusic(opts: MusicOptions): MusicHandle {
  const doc = opts.doc ?? document;
  const musicBtn = doc.getElementById('music-button');
  const slider = doc.getElementById('music-slider') as HTMLInputElement | null;

  let muted = loadMuted(doc);
  let level = loadVolume(doc);

  /** Paint the mute button + slider to match the current mute + level state. */
  const reflect = (): void => {
    if (musicBtn) {
      musicBtn.setAttribute('aria-pressed', String(muted));
      musicBtn.setAttribute('aria-label', muted ? 'Unmute music' : 'Mute music');
      musicBtn.title = muted ? 'Music off — click to unmute' : 'Music on — click to mute';
      // A musical note when audible, a crossed speaker when muted or at zero level.
      musicBtn.textContent = muted || level <= 0 ? '🔇' : '🎵';
    }
    if (slider) {
      const pct = Math.round(level * 100);
      slider.value = String(pct);
      slider.setAttribute('aria-valuenow', String(pct));
      slider.setAttribute('aria-valuetext', `${pct}%`);
    }
  };

  /** Move mute to `next`, optionally persisting, then reflect + notify the music player. */
  const applyMute = (next: boolean, persist: boolean): void => {
    muted = next;
    if (persist) saveMuted(doc, muted);
    reflect();
    opts.onMuteChange(muted);
  };

  /** Move the volume to `next` (`[0, 1]`), optionally persisting, then reflect + notify. */
  const applyVolume = (next: number, persist: boolean): void => {
    level = clamp01(next);
    if (persist) saveVolume(doc, level);
    reflect();
    opts.onVolumeChange?.(level);
  };

  musicBtn?.addEventListener('click', () => applyMute(!muted, true));

  slider?.addEventListener('input', () => {
    const pct = Number(slider.value);
    applyVolume(Number.isFinite(pct) ? pct / 100 : level, true);
  });

  // Paint the saved state and push it to the music player up front, so a player who
  // muted / turned the music down last session has their mix restored before login.
  reflect();
  opts.onMuteChange(muted);
  opts.onVolumeChange?.(level);

  return {
    isMuted: () => muted,
    setMuted: (next) => applyMute(next, true),
    getVolume: () => level,
  };
}
